import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../store';
import { fetchDeletedTenants, restoreTenant, permanentlyDeleteTenant } from '../store/slices/tenantSlice';
import { addNotification, showModal } from '../store/slices/uiSlice';
import { FiArrowLeft, FiRefreshCw, FiTrash2 } from 'react-icons/fi';

const TenantsDeletedPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { deletedTenants, loadingTenant, error } = useSelector((state: RootState) => state.tenants);

    useEffect(() => {
        dispatch(fetchDeletedTenants());
    }, [dispatch]);

    const handleRestore = (id: string, tenantId: string) => {
        dispatch(
            showModal({
                title: 'Confirm Restoration',
                content: `Are you sure you want to restore tenant "${tenantId}"?`,
                confirmLabel: 'Restore',
                cancelLabel: 'Cancel',
                onConfirm: async () => {
                    try {
                        await dispatch(restoreTenant(id)).unwrap();

                        dispatch(
                            addNotification({
                                type: 'success',
                                message: `Tenant "${tenantId}" was successfully restored.`,
                            })
                        );
                    } catch (error: any) {
                        dispatch(
                            addNotification({
                                type: 'error',
                                title: 'Restoration Failed',
                                message: error.message || 'Failed to restore tenant.',
                            })
                        );
                    }
                },
            })
        );
    };

    const handlePermanentDelete = (id: string, tenantId: string) => {
        dispatch(
            showModal({
                title: 'Confirm Permanent Deletion',
                content: `Are you sure you want to permanently delete tenant "${tenantId}"? This action cannot be undone.`,
                confirmLabel: 'Delete Permanently',
                cancelLabel: 'Cancel',
                onConfirm: async () => {
                    try {
                        await dispatch(permanentlyDeleteTenant(id)).unwrap();

                        dispatch(
                            addNotification({
                                type: 'success',
                                message: `Tenant "${tenantId}" was permanently deleted.`,
                            })
                        );
                    } catch (error: any) {
                        dispatch(
                            addNotification({
                                type: 'error',
                                title: 'Deletion Failed',
                                message: error.message || 'Failed to delete tenant permanently.',
                            })
                        );
                    }
                },
            })
        );
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center">
                <button
                    onClick={() => navigate('/tenants')}
                    className="mr-4 inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                    <FiArrowLeft className="mr-2" />
                    Back to Active Tenants
                </button>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Deleted Tenants</h1>
            </div>

            {error && (
                <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-md p-4 text-sm text-red-600 dark:text-red-300">
                    {error}
                </div>
            )}

            <div className="bg-white dark:bg-gray-800 shadow overflow-hidden rounded-lg">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Tenant ID
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Subdomain
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Database
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Cluster
                                </th>
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {loadingTenant ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-4 text-center">
                                        <div className="flex justify-center">
                                            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary-500"></div>
                                        </div>
                                    </td>
                                </tr>
                            ) : deletedTenants.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                                        No deleted tenants found.
                                    </td>
                                </tr>
                            ) : (
                                deletedTenants.map((tenant) => (
                                    <tr key={tenant._id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                            {tenant.tenantId}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                            {tenant.baseUrl}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                            {tenant.databaseName}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                            {tenant.cluster}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex justify-end space-x-3">
                                                <button
                                                    onClick={() => handleRestore(tenant._id, tenant.tenantId)}
                                                    className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                                                    title="Restore tenant"
                                                >
                                                    <FiRefreshCw className="h-5 w-5" />
                                                </button>
                                                <button
                                                    onClick={() => handlePermanentDelete(tenant._id, tenant.tenantId)}
                                                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                                    title="Permanently delete tenant"
                                                >
                                                    <FiTrash2 className="h-5 w-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default TenantsDeletedPage;